import {ChangeDetectorRef, Component, HostListener, Inject, OnChanges, OnInit, ViewChild, DOCUMENT} from '@angular/core';
import {MatTableDataSource} from '@angular/material/table';
import {TranslationService} from '@shared/services/translation.service';
import {MatDialog} from '@angular/material/dialog';

import {ActivatedRoute, Router} from '@angular/router';
import {TopicIndex} from '@shared/models/topic-index.model';
import {SchoolingOnlineService} from '@shared/services/schooling-online.service';
import {TopicIndexService} from '@shared/services/topic-index.service';
import {DEFAULT_PAGE_SIZE, isMobileViewport, PAGE_SIZE_OPTIONS, pageChange} from '@shared/utils/utils';
import {filter, tap} from 'rxjs/operators';
import {MatSort} from '@angular/material/sort';
import {MatPaginator} from '@angular/material/paginator';
import {UntypedFormControl, UntypedFormGroup, Validators} from '@angular/forms';
import {TopicBookletService} from '@shared/services/topic-booklet.service';
import {TopicQuestionaryService} from '@shared/services/topic-questionary.service';
import {MatSnackBar} from '@angular/material/snack-bar';
import {TopicService} from '@shared/services/topic.service';
import {SetCurrentBreadcrumb} from '@shared/store/breadcrumb/breadcrumb.actions';
import {Store} from '@ngxs/store';
import {SchoolingOnline} from '@shared/models/schooling-online.model';
import {DataResponse} from '@shared/models/response.model';
import {InspectionListItem} from '@shared/models/inspection-list-item.model';
import {ConfirmDialogComponent, ConfirmDialogConfig} from '@shared/components/confirm-dialog/confirm-dialog.component';
import {SchoolingPlanService} from '@shared/services/schooling-plan.service';
import {PreviousRouteService} from '@shared/services/previous-route.service';

@Component({
    selector: 'saf-schooling-online-start',
    templateUrl: './schooling-online-start.component.html',
    styleUrls: ['./schooling-online-start.component.scss'],
    standalone: false
})
export class SchoolingOnlineStartComponent implements OnInit {
  protected readonly pageChange = pageChange;
  protected readonly isMobileViewport = isMobileViewport;
  @ViewChild(MatSort, { static: true }) sort: MatSort;
  @ViewChild(MatPaginator, { static: true }) paginator: MatPaginator;
  formGroup: UntypedFormGroup;
  public planId: number;
  public schoolingOnlineId: number;
  public topicId: number;
  public employeeId: number = null;
  public externalEmployeeId: number = null;
  public bookletId: number;
  public languageId: number = null;
  public lastIndexId: number;
  public tableColumns = ['number', 'title', 'type'];
  public dataSource = new MatTableDataSource<TopicIndex>();
  public drawerFilterParams: any = {};
  public searchFilterParams: any = {};
  public loading = false;
  public contentLoading = false;
  public totalCount = 0;
  public pageNumber: number;
  public lastPage: number;
  public start = false;
  public executionSeconds = 0;
  public progress = 100;
  public progressSteps = 0;
  public seconds = 0;
  public progressInterval: any;
  public previousUrl: string;
  public printCertificateOnFinish: boolean = true;
  quizSuccess: boolean;
  questionSuccess: boolean;
  attempts = 0;
  schoolingAttempts: number;
  maxAttempts: number;
  pageType: number;
  fire = false;
  fold = true;
  isPortrait: any;
  public trackById = (item) => item.id;

  // SchoolingOnline
  constructor(
    private translationService: TranslationService,
    private schoolingOnlineService: SchoolingOnlineService,
    private schoolingPlanService: SchoolingPlanService,
    private topicBookletService: TopicBookletService,
    private topicQuestionaryService: TopicQuestionaryService,
    private changeDetectorRef: ChangeDetectorRef,
    private indexService: TopicIndexService,
    private topicService: TopicService,
    private route: ActivatedRoute,
    private store: Store,
    private snackBar: MatSnackBar,
    private router: Router,
    private dialog: MatDialog,
    private previousRouteService: PreviousRouteService,
    @Inject(DOCUMENT) private document: Document,
    @Inject(PAGE_SIZE_OPTIONS) public pageSizeOptions,
    @Inject(DEFAULT_PAGE_SIZE) public pageSize,
  ) { }

  ngOnInit(): void {
    this.route.queryParams.subscribe(params => {
      this.planId = params.planId;
      this.employeeId = params.employeeId;
      this.externalEmployeeId = params.externalEmployeeId;
      this.topicId = params.topicId;
      this.languageId = params.languageId;
    });
    this.previousUrl = this.previousRouteService.getPreviousUrl();
    this.sort.sortChange.subscribe(() => this.paginator.pageIndex = 0);
    // screen.orientation.lock('landscape');
    this.checkOrientation();
  }

  @HostListener('window:resize', ['$event'])
  onResize() {
    this.checkOrientation();
  }

  checkOrientation() {
    this.isPortrait = window.matchMedia('(orientation: portrait)').matches;
  }

  isLoading(isLoading) {
    this.contentLoading = isLoading;
    if (!isLoading && this.executionSeconds !== 0) {
      this.startExecutionTimer();
    } else {
      clearInterval(this.progressInterval);
    }
  }

  ngAfterViewInit() {
    this.buildForm();
    this.changeDetectorRef.detectChanges();
  }

  getBookletInfo() {
    this.topicBookletService.getById(this.bookletId).subscribe((data: any) => {
      this.maxAttempts = data.maxAttempts;
      this.executionSeconds = data.minExecutionTimeInMinutes;
      this.seconds = data.minExecutionTimeInMinutes;
      this.progressSteps = 100 / this.executionSeconds;
    });
  }

  startExecutionTimer() {
    this.progressInterval = setInterval(() => {
      this.executionSeconds--;
      this.progress -= this.progressSteps;
      if (this.executionSeconds === 0) {
        this.progress = 100;
        clearInterval(this.progressInterval);
      }
    }, 1000);
  }

  buildForm() {
    this.formGroup = new UntypedFormGroup({
      planId: new UntypedFormControl(this.planId, Validators.required),
      employeeId: new UntypedFormControl(this.employeeId),
      externalEmployeeId: new UntypedFormControl(this.externalEmployeeId),
      languageId: new UntypedFormControl(this.languageId),
    });

    if (this.topicId) {
      this.topicService.getById(this.topicId)
        .pipe(tap())
        .subscribe((topic) => {
          this.printCertificateOnFinish = topic.printCertificateOnFinish;
          this.store.dispatch(new SetCurrentBreadcrumb(topic.name));
        });
    }
  }

  loadTable() {
    const params = {
      filter: {
        ...this.searchFilterParams,
        ...this.drawerFilterParams,
        bookletId: this.bookletId,
        active: true
      },
      sort: this.sort.direction ? this.sort.active : '',
      dir: this.sort.direction,
      start: this.paginator.pageIndex * this.paginator.pageSize,
      limit: this.paginator.pageSize,
    };

    this.loading = true;

    this.indexService.getList(params)
      .pipe(tap(() => this.loading = false))
      .subscribe((result) => {
        let match = result.data.find(item => this.lastIndexId === item.id);

        if (!match) {
          match = result.data.find(item => item.pageNumber === 1);
        }

        if (match) {
          this.lastIndexId = match.id;
          this.pageNumber = match.pageNumber;
          this.pageType = match.pageType;
        }
        this.totalCount = result.total;
        this.dataSource.data = result.data;
      });
  }

  reloadTable() {
    TopicIndexService.clearCache();
    this.loadTable();
  }

  getTypeDescription(pageType: number) {
    switch (pageType) {
      case 1: return this.translationService.translate('questionary');
      case 2: return this.translationService.translate('video');
      default: return this.translationService.translate('page');
    }
  }

  getTypeIcon(pageType: number) {
    switch (pageType) {
      case 1: return 'quiz';
      case 2: return 'video_file';
      default: return 'description';
    }
  }

  startSchooling() {
    this.start = true;
    this.schoolingOnlineService.start(this.formGroup.getRawValue()).subscribe((data: DataResponse<SchoolingOnline>) => {
      this.schoolingAttempts = data.data.attempt;
      this.bookletId = data.data.bookletId;
      this.schoolingOnlineId = data.data.id;
      this.lastIndexId = data.data.lastIndexId;
      this.reloadTable();
      this.getBookletInfo();
      // this.startExecutionTimer();
    });
  }

  next() {
    this.questionSuccess = false;
    this.executionSeconds = this.seconds;
    this.pageNumber = this.pageNumber + 1;
    this.dataSource.data.forEach(item => {
      if (item.pageNumber === this.pageNumber) {
        this.pageNumber = item.pageNumber;
        this.lastIndexId = item.id;
        this.pageType = item.pageType;
        if (this.pageType === 1) {
          this.quizSuccess = false;
        }
      }
    });
    const params = {
      indexId: this.lastIndexId
    };
    this.schoolingOnlineService.progress(this.schoolingOnlineId, params).subscribe();
    // this.startExecutionTimer();
  }

  back() {
    this.executionSeconds = 0;
    this.progress = 100;
    clearInterval(this.progressInterval);
    this.pageNumber = this.pageNumber - 1;
    this.dataSource.data.forEach(item => {
      if (item.pageNumber === this.pageNumber) {
        this.pageNumber = item.pageNumber;
        this.lastIndexId = item.id;
        this.pageType = item.pageType;
      }
    });
    const params = {
      indexId: this.lastIndexId
    };
    this.schoolingOnlineService.progress(this.schoolingOnlineId, params).subscribe();
  }

  finish() {
    const newWindow = this.printCertificateOnFinish ? window.open('', '_blank') : null;

    this.schoolingOnlineService.finish(this.schoolingOnlineId).subscribe(() => {
      if (newWindow) {
        newWindow.location.href = this.schoolingPlanService.getPrintURL(this.planId, localStorage.getItem('jwt'));
      }

      if (this.previousUrl.includes('external')) {
        localStorage.clear();
      }

      this.router.navigate([this.previousUrl]);
    });
  }


  showResult() {
    this.fire = true;
  }

  foldTable() {
    this.fold = !this.fold;
  }

  showQuestionResult(result) {
    this.fire = false;

    if (result[1].success) {
      this.quizSuccess = true;
      this.questionSuccess = true;
      if (this.dataSource.data.length !== this.pageNumber)  {
        this.next();
      }
    } else {
      this.executionSeconds = 0;
      this.progress = 100;
      clearInterval(this.progressInterval);

      if (result[1].failureReason === 'noAnswerGiven') {
        this.snackBar.open(this.translationService.translate('noAnswerGiven'));
      }

      this.questionSuccess = false;
      this.attempts++;

      if (this.attempts <= this.maxAttempts || result[1].questionary.maxAttempts  === 0) {
        if (result[1].questionary.incorrectAnswerJumpToIndexId) {
          setTimeout(() => {
            this.lastIndexId = result[1].questionary.incorrectAnswerJumpToIndexId;

            this.dataSource.data.forEach(item => {
              if (item.id === this.lastIndexId) {
                this.pageNumber = item.pageNumber;
                this.lastIndexId = item.id;
                this.pageType = item.pageType;
              }
            });
          }, 3000);
        }
      } else {
        this.schoolingOnlineService.block(this.schoolingOnlineId);
        const data = new ConfirmDialogConfig({
          title: this.translationService.translate('schoolingNotPassed'),
          message: this.translationService.translate('schoolingNotPassedInfo'),
          confirmText: this.translationService.translate('continue'),
          showCancel: true
        });

        this.dialog.open(ConfirmDialogComponent, { data })
          .afterClosed()
          .pipe(filter((confirmed) => confirmed))
          .subscribe(() => this.router.navigate(['dashboard']));
      }
    }
  }

  close() {
    this.router.navigate(['dashboard']);
  }
}
